// src/components/Feature.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Feature = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    setAnimate(true);
  }, []);

  const features = [
    {
      id: 1,
      category: "dashboard",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      title: "Dashboard",
      description: "Centralized command center with real-time overview of all housekeeping operations, key metrics, and performance indicators.",
      benefits: ["Real-time analytics", "Key performance indicators", "Activity overview", "Quick actions panel"],
      route: "/dashboard"
    },
    {
      id: 2,
      category: "room",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      title: "Room Allocation",
      description: "Efficiently assign and manage room assignments for housekeeping staff with priority-based allocation system.",
      benefits: ["Smart room assignment", "Priority-based allocation", "Staff workload balancing", "Real-time updates"],
      route: "/room-allocation"
    },
    {
      id: 3,
      category: "room",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Room Selector",
      description: "Interactive room selection interface for quick status updates and task assignments across different room categories.",
      benefits: ["Interactive map view", "Quick status updates", "Room categorization", "Bulk operations"],
      route: "/room-selector"
    },
    {
      id: 4,
      category: "room",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: "Room Status Card",
      description: "Visual status cards for each room showing cleanliness level, occupancy, and pending maintenance tasks at a glance.",
      benefits: ["Color-coded status", "Quick visual identification", "Maintenance alerts", "Occupancy tracking"],
      route: "/room-status"
    },
    {
      id: 5,
      category: "room",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      title: "Room Checklist",
      description: "Comprehensive digital checklists for room cleaning with customizable templates and completion tracking.",
      benefits: ["Customizable templates", "Completion tracking", "Quality validation", "Photo evidence support"],
      route: "/room-checklist"
    },
    {
      id: 6,
      category: "staff",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      title: "Staff Management",
      description: "Complete staff management system with attendance tracking, performance evaluation, and task assignment capabilities.",
      benefits: ["Attendance tracking", "Performance metrics", "Shift scheduling", "Leave management"],
      route: "/staff-management"
    },
    {
      id: 7,
      category: "task",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      title: "Task Management",
      description: "Streamlined task creation, assignment, and tracking system with deadline management and priority settings.",
      benefits: ["Task prioritization", "Deadline tracking", "Assignment history", "Completion verification"],
      route: "/task-management"
    },
    {
      id: 8,
      category: "cleaning",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      title: "Cleaning Checklist",
      description: "Standardized cleaning checklists with step-by-step verification and quality control measures for consistent results.",
      benefits: ["Standardized procedures", "Step verification", "Quality scoring", "Time tracking"],
      route: "/cleaning-checklist"
    },
    {
      id: 9,
      category: "maintenance",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      title: "Damage Report",
      description: "Quick and detailed damage reporting system with photo uploads, location tagging, and repair tracking capabilities.",
      benefits: ["Photo documentation", "Location tagging", "Repair tracking", "Cost estimation"],
      route: "/damage-report"
    },
    {
      id: 10,
      category: "review",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      title: "Issue Review",
      description: "Centralized platform for reviewing, approving, and resolving reported issues with workflow management.",
      benefits: ["Issue tracking", "Approval workflow", "Resolution monitoring", "Feedback collection"],
      route: "/issue-review"
    },
    {
      id: 11,
      category: "audit",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: "Audit Report",
      description: "Comprehensive audit logs and reports for compliance tracking, performance analysis, and historical data review.",
      benefits: ["Audit trails", "Compliance reports", "Performance analysis", "Historical data"],
      route: "/audit-report"
    },
    {
      id: 12,
      category: "analytics",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: "Statistics",
      description: "Advanced analytics dashboard with customizable reports, trend analysis, and predictive insights for better decision making.",
      benefits: ["Trend analysis", "Predictive insights", "Custom reports", "Data visualization"],
      route: "/statistics"
    }
  ];

  const categories = [
    { id: 'all', name: 'All Features' },
    { id: 'dashboard', name: 'Dashboard' },
    { id: 'room', name: 'Room Management' },
    { id: 'staff', name: 'Staff Management' },
    { id: 'task', name: 'Task Management' },
    { id: 'cleaning', name: 'Cleaning' },
    { id: 'maintenance', name: 'Maintenance' },
    { id: 'review', name: 'Reviews' },
    { id: 'audit', name: 'Audit' },
    { id: 'analytics', name: 'Analytics' }
  ];

  const handleFeatureClick = (route) => {
    if (route) {
      navigate(route);
    }
  };

  const filteredFeatures = activeTab === 'all' 
    ? features 
    : features.filter(f => f.category === activeTab);

  const stats = [
    { number: "99.9%", label: "Uptime", icon: "⚡" },
    { number: "50K+", label: "Rooms Managed", icon: "🏨" },
    { number: "24/7", label: "Support", icon: "🎧" },
    { number: "1000+", label: "Happy Clients", icon: "😊" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      {/* Hero Section */}
      <div className="relative bg-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-4 transition-all duration-500 ${animate ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
              <span className="mr-2">✨</span>
              Complete Housekeeping Management Suite
            </div>
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 transition-all duration-500 delay-100 ${animate ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
              Everything You Need for
              <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent"> Modern Housekeeping</span>
            </h1>
            <p className={`text-xl text-gray-600 max-w-3xl mx-auto mb-8 transition-all duration-500 delay-200 ${animate ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
              From room allocation to audit reports, our comprehensive platform streamlines all housekeeping operations in one place
            </p>
            
            {/* CTA Buttons */}
            <div className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-500 delay-300 ${animate ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
              {/* <button 
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-105 shadow-lg"
              >
                Go to Dashboard
              </button> */}
              <button className="px-6 py-3 border-2 border-blue-600 text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all">
                Watch Demo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-white py-12 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className={`text-center transition-all duration-500 delay-${index * 100} ${animate ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                <div className="text-3xl mb-2">{stat.icon}</div>
                <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-1">{stat.number}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Complete Feature Set
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover all the powerful features available in your housekeeping management dashboard
          </p>
        </div>

        {/* Category Tabs */}
       <div className="flex flex-nowrap overflow-x-auto justify-start gap-2 mb-12 pb-2 
     [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
  {categories.map((category) => (
    <button
      key={category.id}
      onClick={() => setActiveTab(category.id)}
      className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
        activeTab === category.id
          ? 'bg-blue-600 text-white shadow-lg transform scale-105'
          : 'bg-white text-gray-600 hover:bg-blue-50 border border-gray-200'
      }`}
    >
      {category.name}
    </button>
  ))}
</div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredFeatures.map((feature, index) => (
            <div
              key={feature.id}
              onClick={() => handleFeatureClick(feature.route)}
              className={`group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 animate-fade-in-up cursor-pointer`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                {feature.title}
              </h3>
              
              {/* Description */}
              <p className="text-gray-600 mb-4 leading-relaxed">
                {feature.description}
              </p>
              
              {/* Benefits List */}
              <div className="space-y-2">
                {feature.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center text-sm text-gray-500">
                    <svg className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
              
              {/* View Button */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <span className="text-blue-600 text-sm font-medium group-hover:text-blue-700 transition-colors flex items-center">
                  Access Feature
                  <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-16 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Housekeeping Operations?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Access all these powerful features and more in your dashboard today
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
           
            <button className="px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all">
              Schedule Demo
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default Feature;